import { CloseModalButton } from "~/components/modal";
import { Accessor, createEffect, createSignal, Setter, Show } from "solid-js";
import { ThreeDotLoader } from "~/widgets/button";
import { goBack } from "~/shared_states/modal";
import { useTelescopeContext } from "@hubble/telescope";

export type ProfileUpdateParams = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
};

type AxisUserInfoModalProps = {
  onSubmit: (profileUpdateParams: ProfileUpdateParams) => Promise<void>;
  email: string | null;
  phone: string | null;
  name: string | null;
  redemptionAmount: number;
  brandName: string;
  brandLogoUrl: string;
  showLoader: Accessor<boolean>;
  setShowLoader: Setter<boolean>;
  errors: Accessor<{ [key: string]: string }>;
  setErrors: Setter<{ [key: string]: string }>;
  onClose: () => void;
};

export function AxisUserInfoModal(props: AxisUserInfoModalProps) {
  const { components } = useTelescopeContext();

  const [name, setName] = createSignal<string>(props.name ?? "");
  const [email, setEmail] = createSignal<string>(props.email ?? "");
  const [phone, setPhone] = createSignal<string>(props.phone ?? "");
  const [hasSubmittedOnce, setHasSubmittedOnce] = createSignal<boolean>(false);

  let nameInput: HTMLInputElement | undefined;
  let emailInput: HTMLInputElement | undefined;
  let phoneInput: HTMLInputElement | undefined;

  const { errors, setErrors } = props;

  function isNameAlreadyPresent() {
    return !!props.name;
  }

  function isEmailAlreadyPresent() {
    return !!props.email;
  }

  function isPhoneAlreadyPresent() {
    return !!props.phone;
  }

  const validateName = (name: string) => {
    const nameRegex = /^[A-Za-z\s]{2,50}$/;
    return nameRegex.test(name.trim());
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^[1-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validateAndUpdateErrors = () => {
    const newErrors: { [key: string]: string } = {};

    if (!name()) {
      newErrors["name"] = "Please enter your full name";
    } else if (!validateName(name())) {
      newErrors["name"] = "Please enter your full name";
    }

    if (!email()) {
      newErrors["email"] = "Please enter your email address";
    } else if (!validateEmail(email())) {
      newErrors["email"] = "Please enter a valid email address";
    }

    // skip phone validation for now
    if (!phone()) {
      newErrors["phone"] = "Please enter your phone no.";
    } else if (!validatePhone(phone())) {
      newErrors["phone"] = "Please enter a valid phone no.";
    }

    setErrors(newErrors);
    return newErrors;
  };

  // Auto-validate after first submit attempt
  createEffect(() => {
    if (hasSubmittedOnce()) {
      name();
      email();
      phone();
      validateAndUpdateErrors();
    }
  });

  const scrollIntoViewSmooth = (element: HTMLElement) => {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const updateUserInfo = async () => {
    let body: ProfileUpdateParams = {};
    if (!isNameAlreadyPresent() && name()) {
      const cleanedName = name().trim().replace(/\s+/g, " ");
      body = {
        ...body,
        firstName: cleanedName.split(" ")[0],
        lastName: cleanedName.split(" ").slice(1).join(" "),
      };
    }
    if (!isEmailAlreadyPresent() && email()) {
      body = {
        ...body,
        email: email(),
      };
    }
    if (!isPhoneAlreadyPresent() && phone()) {
      body = {
        ...body,
        phoneNumber: phone(),
      };
    }

    await props.onSubmit(body);
  };

  const onSubmit = async (e: Event) => {
    e.preventDefault();

    // Mark that user has attempted to submit
    setHasSubmittedOnce(true);

    // Validate all fields
    const currentErrors = validateAndUpdateErrors();

    // If there are errors, focus on the first error field
    if (Object.keys(currentErrors).length > 0) {
      const firstError = Object.keys(currentErrors)[0];
      if (firstError === "name" && nameInput) {
        scrollIntoViewSmooth(nameInput);
        nameInput.focus();
      } else if (firstError === "email" && emailInput) {
        scrollIntoViewSmooth(emailInput);
        emailInput.focus();
      } else if (firstError === "phone" && phoneInput) {
        scrollIntoViewSmooth(phoneInput);
        phoneInput.focus();
      }
      return;
    }

    // Submit the form
    await updateUserInfo();
  };

  return (
    <form onSubmit={onSubmit}>
      <div class="flex flex-col bg-baseTertiaryLight text-basePrimaryDark">
        {/* Header */}
        <div class="flex w-full flex-row items-start justify-between px-6 pb-2 pt-6">
          <span class="text-h4 text-basePrimaryDark">
            Just one more step
            <br />
            <span class="text-medium text-textNormal">
              Gift card will be sent on your email and phone number
            </span>
          </span>
          <CloseModalButton iconSize={20} onClick={props.onClose} />
        </div>

        {/* Form Fields */}
        <div class="flex flex-col gap-4 px-4 py-1 md:px-6">
          {/* Name Input */}
          <components.Input
            ref={nameInput}
            value={name()}
            onChange={(value: string) => setName(value)}
            placeholder="Enter your full name"
            size={40}
            type="text"
            label="Your full name"
            state={
              errors()["name"]
                ? "error"
                : isNameAlreadyPresent()
                  ? "disabled"
                  : "default"
            }
            helperText={errors()["name"]}
          />

          {/* Email Input */}
          <components.Input
            ref={emailInput}
            value={email()}
            onChange={(value: string) => setEmail(value)}
            placeholder="Enter your email id"
            size={40}
            type="text"
            label="Your email"
            state={
              errors()["email"]
                ? "error"
                : isEmailAlreadyPresent()
                  ? "disabled"
                  : "default"
            }
            helperText={
              errors()["email"] || "Gift card will be sent on this email"
            }
          />

          {/* Phone Input */}
          <components.Input
            ref={phoneInput}
            label="Your phone no."
            value={phone()}
            onChange={(value: string) => setPhone(value)}
            placeholder="Enter your phone no."
            size={40}
            type="phone"
            state={
              errors()["phone"]
                ? "error"
                : isPhoneAlreadyPresent()
                  ? "disabled"
                  : "default"
            }
            helperText={
              errors()["phone"] || "Gift card will be sent on this phone number"
            }
          />
        </div>

        {/* Privacy Text */}
        <div class="px-4 pb-5 text-f12 text-textNormal md:px-6">
          Your privacy is our priority. We never share your data with anyone
        </div>

        {/* Sticky Footer */}
        <div class=" sticky bottom-0 z-10 mt-auto flex w-full flex-col items-center gap-1 border-t border-baseTertiaryDark bg-baseTertiaryLight px-4 py-4 md:px-6">
          <button
            type="submit"
            onClick={onSubmit}
            disabled={props.showLoader()}
            class="button-filled flex h-[50px] w-full items-center justify-center  rounded-full bg-basePrimaryDark text-f16Bold text-baseTertiaryLight disabled:opacity-50"
          >
            <Show when={!props.showLoader()} fallback={<ThreeDotLoader />}>
              Continue
            </Show>
          </button>
        </div>
      </div>
    </form>
  );
}
